import { useWatched } from '../DiscoveryPreferences';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiArrowDown, FiArrowUp, FiFilm, FiThumbsUp, FiSlash, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Api_key from '../apiconfiguration';
import useArtworkTheme from '../useArtworkTheme';
import { WatchlistButton } from '../LibraryActions';
import './MovieShorts.css';
import { getMovieClips } from '../movieClips';
import ReelPlayer from '../ReelPlayer';
import { useAuth } from '../AuthContext';
import { loadGuestTaste, rankShorts } from '../shortTaste';

const feedCache = new Map();


function Preview({ movie }) {
  const [result, setResult] = useState({ id: null, clips: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [clipIndex, setClipIndex] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(false); setClipIndex(0);
    getMovieClips(movie, controller.signal)
      .then(clips => { if (!controller.signal.aborted) setResult({ id: movie.id, clips }); })
      .catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [movie, attempt]);
  const pending = loading || result.id !== movie.id;
  const preview = result.clips[clipIndex];
  const changeClip = direction => setClipIndex(index => (index + direction + result.clips.length) % result.clips.length);
  return <div className="short-preview">
    <ReelPlayer video={preview?.key} title={movie.title} vertical={preview?.vertical} suspended={pending || error || !preview} onClipChange={changeClip} hasMoreClips={result.clips.length > 1} />
    {pending && !error ? <div className="short-preview-status" role="status">Finding movie clips…</div> : preview && !error ?
      <div className="short-clip-info">
        <div className="short-clip-caption"><span>{preview.label}</span><small>{result.clips.length > 1 ? 'Swipe sideways for more clips' : 'Swipe up for another movie'}</small></div>
        {result.clips.length > 1 && <nav className="clip-switcher" aria-label="Clips for this movie"><button aria-label="Previous clip" onClick={() => changeClip(-1)}><FiChevronLeft /></button><span role="status" aria-live="polite">{clipIndex + 1}<i> / {result.clips.length}</i></span><button aria-label="Next clip" onClick={() => changeClip(1)}><FiChevronRight /></button></nav>}
      </div>
      : <div className="short-preview-status"><FiFilm /><p>{error ? 'Clips could not load.' : 'No clips available for this movie yet.'}</p>{error && <button onClick={() => setAttempt(value => value + 1)}>Try again</button>}<span>Explore the movie below or swipe to the next story.</span></div>}
  </div>;
}

export default function MovieShorts() {
  const { user } = useAuth();
  return <ShortsFeed key={user?.uid || 'guest'} owner={user?.uid || 'guest'} />;
}

function ShortsFeed({ owner }) {
  const {watched,hideWatched} = useWatched();
  const { user, experience, saveExperienceItem } = useAuth();
  const savedFeed = feedCache.get(owner);
  const [localSignals, setLocalSignals] = useState(() => user ? {} : loadGuestTaste());
  const signals = { ...(user ? experience.shortTaste || {} : {}), ...localSignals };
  const signalsRef = useRef(signals);
  signalsRef.current = signals;
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  useEffect(() => {
    if (!feedbackMessage) return undefined;
    const timer = setTimeout(() => setFeedbackMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [feedbackMessage]);
  const [movies, setMovies] = useState(() => savedFeed?.movies || []);
  const [page, setPage] = useState(() => savedFeed?.page || 1);
  const [active, setActive] = useState(() => savedFeed?.active || 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const feed = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  useEffect(() => {
    const element = feed.current;
    const observer = new ResizeObserver(() => { element.scrollTop = element.clientHeight * activeRef.current; });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const restoreIndex = useRef(savedFeed?.active || 0);
  const restored = useRef(false);
  const pendingIndex = useRef(null);
  const hiddenKeys = Object.entries(signals).filter(([,signal]) => signal.preference === 'hide').map(([key]) => key).concat(hideWatched ? [...watched] : []).join(',');
  useArtworkTheme(movies[active]?.backdrop_path || movies[active]?.poster_path);

  // Account preferences can arrive after the first page has loaded.
  useEffect(() => {
    const hidden = new Set(hiddenKeys.split(','));
    const filtered = movies.filter(movie => !hidden.has(`movie_${movie.id}`));
    if (filtered.length === movies.length) return;
    const currentId = movies[active]?.id;
    const found = filtered.findIndex(movie => movie.id === currentId);
    const index = found >= 0 ? found : Math.min(active, Math.max(0, filtered.length - 1));
    pendingIndex.current = index;
    setMovies(filtered); setActive(index);
  }, [hiddenKeys, movies, active]);

  useEffect(() => {
    if (movies.length) feedCache.set(owner, { movies, page, active });
  }, [movies, page, active, owner]);

  useLayoutEffect(() => {
    if (pendingIndex.current !== null) {
      feed.current.scrollTop = feed.current.clientHeight * pendingIndex.current;
      pendingIndex.current = null;
    }
    if (!restored.current && movies.length) {
      feed.current.scrollTop = feed.current.clientHeight * restoreIndex.current;
      restored.current = true;
    }
  }, [movies.length]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(false);
    axios.get('https://api.themoviedb.org/3/discover/movie', { params: { api_key: Api_key, page, include_adult: false, sort_by: 'popularity.desc', 'vote_count.gte': 20, ...(page % 3 !== 0 ? { with_genres: [...new Set(Object.values(signalsRef.current).filter(signal => signal.preference === 'more').flatMap(signal => signal.genreIds || []))].slice(0, 5).join('|') || undefined } : {}) }, signal: controller.signal, timeout: 12000 })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setMovies(previous => {
          const known = new Set(previous.map(movie => movie.id));
          return [...previous, ...rankShorts(data.results || [], signalsRef.current).filter(movie => !known.has(movie.id))];
        });
        setHasMore(page < Math.min(data.total_pages || 1, 500));
      })
      .catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, attempt]);

  useEffect(() => {
    if (!loading && !error && hasMore && active >= movies.length - 4) {
      setLoading(true);
      setPage(value => value + 1);
    }
  }, [active, movies.length, loading, error, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting && entry.intersectionRatio > .6) setActive(Number(entry.target.dataset.index)); });
    }, { root: feed.current, threshold: [.6, .8] });
    feed.current.querySelectorAll('.short-slide').forEach(slide => observer.observe(slide));
    return () => observer.disconnect();
  }, [movies.length]);

  const move = direction => {
    const target = feed.current.querySelector(`[data-index="${active + direction}"]`);
    target?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  const reactToMovie = async preference => {
    const movie = movies[active];
    if (!movie || feedbackBusy) return;
    setFeedbackBusy(true); setFeedbackMessage('');
    const key = `movie_${movie.id}`;
    const value = { id: movie.id, preference, genreIds: movie.genre_ids || [] };
    const nextSignals = { ...signalsRef.current, [key]: value };
    try {
      if (user) {
        if (!await saveExperienceItem('shortTaste', key, value)) return;
      } else localStorage.setItem('hedone-short-taste', JSON.stringify(nextSignals));
      setLocalSignals(previous => ({ ...previous, [key]: value }));
      const remaining = rankShorts(movies.slice(active + 1), nextSignals);
      if (preference === 'hide') {
        setMovies([...movies.slice(0, active), ...remaining]);
        setActive(Math.min(active, Math.max(0, movies.length - 2)));
        setFeedbackMessage('Hidden. We will show fewer movies like this.');
      } else {
        setMovies([...movies.slice(0, active + 1), ...remaining]);
        setFeedbackMessage('Got it. More movies like this are moving up your feed.');
      }
    } catch { setFeedbackMessage('Could not save your preference. Please try again.'); }
    finally { setFeedbackBusy(false); }
  };
  return <div className="shorts-page">
    <header className="shorts-heading"><div><span className="section-kicker">A LITTLE PREVIEW. A NEW OBSESSION.</span><h1>Shorts<span>For you</span></h1></div>{error ? <button className="shorts-retry" onClick={() => setAttempt(value => value + 1)}>Retry loading movies</button> : <Link to="/">Back to discover</Link>}</header>
    <div className="shorts-feed" ref={feed} tabIndex={0} aria-label="Movie previews. Scroll or use the up and down arrow keys to browse." onKeyDown={event => {
      if (event.target !== event.currentTarget || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault(); move(event.key === 'ArrowDown' ? 1 : -1);
    }}>
      {movies[active] && <div className="short-player-dock" style={{ top: `${active * 100}%` }}><Preview movie={movies[active]} /></div>}
      {movies.map((movie, index) => <section key={movie.id} className="short-slide" data-index={index} aria-label={`${movie.title}, preview ${index + 1}`} aria-hidden={index !== active} inert={index !== active ? '' : undefined}>
        {Math.abs(index - active) <= 1 && <div className="short-card">
          <div className="short-backdrop" style={{ backgroundImage: movie.poster_path ? `url(https://image.tmdb.org/t/p/w500${movie.poster_path})` : undefined }} />


          <div className="reel-social"><button disabled={feedbackBusy} onClick={() => reactToMovie('more')} aria-pressed={signals[`movie_${movie.id}`]?.preference === 'more'}><FiThumbsUp /><span>More like this</span></button><button disabled={feedbackBusy} onClick={() => reactToMovie('hide')}><FiSlash /><span>Not interested</span></button><WatchlistButton item={movie} mediaType="movie" compact /></div>
          <div className="short-copy"><div className="short-actions"><Link to={`/movie/${movie.id}`} state={{ watchNow: true }} className="short-watch">Watch now</Link><Link className="short-details" to={`/movie/${movie.id}`}>Details</Link></div></div>
        </div>}
      </section>)}
      <div className="shorts-status" role="status">{loading ? 'Finding more stories…' : error ? 'Couldn’t load more movies. Use Retry above to continue from here.' : !hasMore ? 'You’re all caught up. More movies will be here soon.' : null}</div>
    </div>
    {feedbackMessage && <div className="short-feedback" role="status">{feedbackMessage}</div>}
    <nav className="shorts-controls" aria-label="Browse previews"><button aria-label="Previous movie" disabled={active === 0} onClick={() => move(-1)}><FiArrowUp /></button><button aria-label="Next movie" disabled={active >= movies.length - 1} onClick={() => move(1)}><FiArrowDown /></button></nav>
  </div>;
}
